import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { getAllCompanies } from "../../lib/supabase/companies";
import {
    FiGrid, FiUsers, FiBarChart2, FiFileText, FiCreditCard,
    FiTrendingUp, FiDollarSign, FiCheckCircle, FiClock,
    FiAlertCircle, FiSettings, FiActivity, FiZap, FiStar
} from "react-icons/fi";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const OwnerDashboard = () => {
    const navigate = useNavigate();
    const { companySlug } = useParams();
    const { userProfile, company } = useAuth();
    const [stats, setStats] = useState({
        totalCompanies: null,
        activeCompanies: null,
        totalRevenue: null,
        enterpriseClients: null
    });
    const [recentActivity, setRecentActivity] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const companies = await getAllCompanies();

            // Calculate stats
            const totalCompanies = companies.length;
            const activeCompanies = companies.filter(c => c.is_active).length;
            const totalRevenue = companies.reduce((sum, c) => sum + (parseFloat(c.base_price) || 0), 0);
            const enterpriseClients = companies.filter(c => c.subscription_plan === 'enterprise').length;

            setStats({
                totalCompanies,
                activeCompanies,
                totalRevenue,
                enterpriseClients
            });

            // Mock recent activity (in production, fetch from activity logs)
            setRecentActivity([
                { id: 1, action: 'New company created', company: companies[0]?.name, time: '2 hours ago', type: 'success' },
                { id: 2, action: 'Subscription upgraded', company: companies[1]?.name, time: '5 hours ago', type: 'info' },
                { id: 3, action: 'Payment received', company: companies[2]?.name, time: '1 day ago', type: 'success' }
            ]);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const quickActions = [
        {
            title: 'Manage Companies',
            description: 'View, edit, and onboard client companies',
            icon: FiGrid,
            path: `/${companySlug}/companies`,
            color: 'blue',
            gradient: 'from-blue-500 to-blue-600'
        },
        {
            title: 'Manage Users',
            description: 'Oversee all user accounts and roles',
            icon: FiUsers,
            path: `/${companySlug}/users`,
            color: 'purple',
            gradient: 'from-purple-500 to-purple-600'
        },
        {
            title: 'Communication Hub',
            description: 'Send messages and notifications to users',
            icon: FiActivity,
            path: `/${companySlug}/communicate`,
            color: 'indigo',
            gradient: 'from-indigo-500 to-indigo-600'
        },
        {
            title: 'System Analytics',
            description: 'Monitor performance and usage metrics',
            icon: FiBarChart2,
            path: `/${companySlug}/analytics`,
            color: 'green',
            gradient: 'from-green-500 to-green-600'
        },
        {
            title: 'Activity Logs',
            description: 'Track system-wide actions and changes',
            icon: FiFileText,
            path: `/${companySlug}/activity`,
            color: 'amber',
            gradient: 'from-amber-500 to-amber-600'
        },
        {
            title: 'Billing & Plans',
            description: 'Manage subscriptions and invoices',
            icon: FiCreditCard,
            path: `/${companySlug}/billing`,
            color: 'pink',
            gradient: 'from-pink-500 to-pink-600'
        },
        {
            title: 'Settings',
            description: 'Configure platform settings',
            icon: FiSettings,
            path: `/${companySlug}/settings`,
            color: 'gray',
            gradient: 'from-gray-500 to-gray-600'
        }
    ];

    const handleNavigation = (path) => {
        navigate(path);
    };

    function capitalizeFirstLetter(string) {
        if (!string) return '';
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    if (loading) {
        return (
            <div className="mobile-dashboard-page space-y-8">
                {/* Hero Header Skeleton */}
                <div className="bg-white dark:bg-zinc-800 rounded-2xl p-8 border border-gray-200 dark:border-zinc-700">
                    <div className="flex items-center gap-3 mb-4">
                        <Skeleton circle width={48} height={48} />
                        <div className="flex-1">
                            <Skeleton width={300} height={32} className="mb-2" />
                            <Skeleton width={200} height={16} />
                        </div>
                    </div>
                    <div className="flex items-center gap-3 mt-6">
                        <Skeleton width={100} height={36} borderRadius={8} />
                        <Skeleton width={100} height={36} borderRadius={8} />
                        <Skeleton width={150} height={36} borderRadius={8} />
                    </div>
                </div>

                {/* Stats Grid Skeleton */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[...Array(4)].map((_, index) => (
                        <div key={index} className="bg-white dark:bg-zinc-800 rounded-xl p-6 border border-gray-200 dark:border-zinc-700">
                            <div className="flex items-center justify-between mb-4">
                                <Skeleton circle width={48} height={48} />
                                <Skeleton width={50} height={24} borderRadius={8} />
                            </div>
                            <Skeleton width={120} height={16} className="mb-2" />
                            <Skeleton width={80} height={36} className="mb-2" />
                            <Skeleton width={140} height={12} />
                        </div>
                    ))}
                </div>

                {/* Quick Actions Skeleton */}
                <div className="space-y-4">
                    <Skeleton width={200} height={28} className="mb-4" />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[...Array(6)].map((_, index) => (
                            <div key={index} className="bg-white dark:bg-zinc-800 rounded-xl p-6 border border-gray-200 dark:border-zinc-700">
                                <div className="flex items-start gap-4">
                                    <Skeleton width={48} height={48} borderRadius={12} />
                                    <div className="flex-1">
                                        <Skeleton width={150} height={24} className="mb-2" />
                                        <Skeleton count={2} height={14} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Recent Activity & System Info Skeleton */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Recent Activity Skeleton */}
                    <div className="bg-white dark:bg-zinc-800 rounded-xl p-6 border border-gray-200 dark:border-zinc-700">
                        <div className="flex items-center justify-between mb-6">
                            <Skeleton width={150} height={24} />
                            <Skeleton width={80} height={20} />
                        </div>
                        <div className="space-y-4">
                            {[...Array(3)].map((_, index) => (
                                <div key={index} className="flex items-start gap-3 p-3">
                                    <Skeleton circle width={8} height={8} />
                                    <div className="flex-1">
                                        <Skeleton width={200} height={16} className="mb-2" />
                                        <Skeleton width={150} height={12} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Platform Information Skeleton */}
                    <div className="bg-white dark:bg-zinc-800 rounded-xl p-6 border border-gray-200 dark:border-zinc-700">
                        <Skeleton width={200} height={24} className="mb-6" />
                        <div className="space-y-4">
                            {[...Array(4)].map((_, index) => (
                                <div key={index} className="flex justify-between items-center py-3">
                                    <Skeleton width={100} height={16} />
                                    <Skeleton width={150} height={16} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Hero Header */}
            <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-2xl p-8 text-white">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)'
                }}></div>

                <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                            <FiZap className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold">
                                Welcome back, {capitalizeFirstLetter(userProfile?.username || userProfile?.full_name?.split(' ')[0])}
                            </h1>
                            <p className="text-white/80 text-sm mt-1">Platform Owner Dashboard</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 mt-6">
                        <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                            <div className="flex items-center gap-2">
                                <FiStar className="w-4 h-4 text-amber-300" />
                                <span className="text-sm font-medium">App Owner</span>
                            </div>
                        </div>
                        <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                <span className="text-sm font-medium">Online</span>
                            </div>
                        </div>
                        <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                            <span className="text-sm font-medium">
                                {new Date().toLocaleDateString('en-AU', {
                                    weekday: 'long',
                                    month: 'short',
                                    day: 'numeric'
                                })}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Total Companies */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800 hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-blue-600/10 dark:bg-blue-400/10 rounded-xl flex items-center justify-center">
                            <FiGrid className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="text-xs text-blue-600 dark:text-blue-400 font-medium bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded-lg">
                            +12%
                        </div>
                    </div>
                    <div>
                        <p className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1">Total Companies</p>
                        <p className="text-3xl font-bold text-blue-900 dark:text-blue-100">
                            {stats.totalCompanies !== null ? stats.totalCompanies : <Skeleton width={80} height={36} />}
                        </p>
                        <p className="text-xs text-blue-600/60 dark:text-blue-400/60 mt-2">All registered companies</p>
                    </div>
                </div>

                {/* Active Companies */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-6 border border-green-200 dark:border-green-800 hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-green-600/10 dark:bg-green-400/10 rounded-xl flex items-center justify-center">
                            <FiCheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="text-xs text-green-600 dark:text-green-400 font-medium bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded-lg">
                            Active
                        </div>
                    </div>
                    <div>
                        <p className="text-sm text-green-600 dark:text-green-400 font-medium mb-1">Active Companies</p>
                        <p className="text-3xl font-bold text-green-900 dark:text-green-100">
                            {stats.activeCompanies !== null ? stats.activeCompanies : <Skeleton width={80} height={36} />}
                        </p>
                        <p className="text-xs text-green-600/60 dark:text-green-400/60 mt-2">Currently operational</p>
                    </div>
                </div>

                {/* Total Revenue */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-6 border border-purple-200 dark:border-purple-800 hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-purple-600/10 dark:bg-purple-400/10 rounded-xl flex items-center justify-center">
                            <FiDollarSign className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div className="text-xs text-purple-600 dark:text-purple-400 font-medium bg-purple-100 dark:bg-purple-900/30 px-2 py-1 rounded-lg">
                            MRR
                        </div>
                    </div>
                    <div>
                        <p className="text-sm text-purple-600 dark:text-purple-400 font-medium mb-1">Monthly Revenue</p>
                        <p className="text-3xl font-bold text-purple-900 dark:text-purple-100">
                            {stats.totalRevenue !== null ? `$${stats.totalRevenue.toLocaleString()}` : <Skeleton width={120} height={36} />}
                        </p>
                        <p className="text-xs text-purple-600/60 dark:text-purple-400/60 mt-2">Recurring monthly</p>
                    </div>
                </div>

                {/* Enterprise Clients */}
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 rounded-xl p-6 border border-amber-200 dark:border-amber-800 hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-amber-600/10 dark:bg-amber-400/10 rounded-xl flex items-center justify-center">
                            <FiTrendingUp className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div className="text-xs text-amber-600 dark:text-amber-400 font-medium bg-amber-100 dark:bg-amber-900/30 px-2 py-1 rounded-lg">
                            Premium
                        </div>
                    </div>
                    <div>
                        <p className="text-sm text-amber-600 dark:text-amber-400 font-medium mb-1">Enterprise Plans</p>
                        <p className="text-3xl font-bold text-amber-900 dark:text-amber-100">
                            {stats.enterpriseClients !== null ? stats.enterpriseClients : <Skeleton width={80} height={36} />}
                        </p>
                        <p className="text-xs text-amber-600/60 dark:text-amber-400/60 mt-2">Premium tier clients</p>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <FiActivity className="w-6 h-6 text-blue-600" />
                        Quick Actions
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {quickActions.map((action) => (
                        <button
                            key={action.title}
                            onClick={() => handleNavigation(action.path)}
                            className="group relative overflow-hidden bg-white dark:bg-zinc-800 hover:bg-gray-50 dark:hover:bg-zinc-700 border border-gray-200 dark:border-zinc-700 rounded-xl p-6 text-left transition-all duration-300 hover:shadow-xl hover:scale-[1.02]"
                        >
                            {/* Gradient overlay on hover */}
                            <div className={`absolute inset-0 bg-gradient-to-br ${action.gradient} opacity-0 group-hover:opacity-5 transition-opacity`}></div>

                            <div className="relative z-10">
                                <div className="flex items-start gap-4">
                                    <div className={`w-12 h-12 bg-${action.color}-50 dark:bg-${action.color}-900/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                                        <action.icon className={`w-6 h-6 text-${action.color}-600 dark:text-${action.color}-400`} />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-gray-900 dark:text-white text-lg mb-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                            {action.title}
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                                            {action.description}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Bottom accent line */}
                            <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${action.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}></div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Recent Activity & System Info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Activity */}
                <div className="bg-white dark:bg-zinc-800 rounded-xl p-6 border border-gray-200 dark:border-zinc-700">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <FiActivity className="w-5 h-5 text-blue-600" />
                            Recent Activity
                        </h3>
                        <button className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium">
                            View All
                        </button>
                    </div>

                    <div className="space-y-4">
                        {recentActivity.length > 0 ? recentActivity.map((activity) => (
                            <div key={activity.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-700/50 transition-colors">
                                <div className={`w-2 h-2 rounded-full mt-2 ${
                                    activity.type === 'success' ? 'bg-green-500' :
                                    activity.type === 'info' ? 'bg-blue-500' : 'bg-gray-500'
                                }`}></div>
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {activity.action}
                                    </p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                        {activity.company} • {activity.time}
                                    </p>
                                </div>
                            </div>
                        )) : (
                            <p className="text-center text-gray-500 dark:text-gray-400 py-8">No recent activity</p>
                        )}
                    </div>
                </div>

                {/* Platform Information */}
                <div className="bg-white dark:bg-zinc-800 rounded-xl p-6 border border-gray-200 dark:border-zinc-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                        <FiSettings className="w-5 h-5 text-purple-600" />
                        Platform Information
                    </h3>

                    <div className="space-y-4">
                        <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-zinc-700">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Platform Owner</span>
                            <span className="font-medium text-gray-900 dark:text-white">
                                {userProfile?.username || userProfile?.full_name}
                            </span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-zinc-700">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Email</span>
                            <span className="font-medium text-gray-900 dark:text-white text-sm">
                                {userProfile?.email}
                            </span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-zinc-700">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Company</span>
                            <span className="font-medium text-gray-900 dark:text-white">
                                {company?.name}
                            </span>
                        </div>
                        <div className="flex justify-between items-center py-3">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Timezone</span>
                            <span className="font-medium text-gray-900 dark:text-white text-sm">
                                {company?.timezone || 'Australia/Melbourne'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OwnerDashboard;
